import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Alert, Box, Checkbox, FieldSet, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';

import {
  useDebouncedMultiplexAmenitySave,
  useIsOnReviewStep,
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
  useMultiplexUpdate,
} from '../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { useMultiEditMutation } from '../../../hooks/useMultiEditMutation';
import { getDataToSave } from '../../../utils/amenitiesUtils';

import DisabledAccessAmenityTooltip from '../../DisabledAccessAmenityTooltip';

import type { ChangeEvent } from 'react';
import type { GetUserPropertyDetailsAmenitiesGroupWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

type AmenityGroup = GetUserPropertyDetailsAmenitiesGroupWebDto;

const buildInitialState = (property: Array<AmenityGroup>) => {
  const categorizedAmenitiesKeys: Record<string, Array<string>> = { checkboxes: [] };
  const initialInputs: Record<string, boolean | string> = property.reduce(
    (a, { category, isUniqueSelection, values }) => {
      if (isUniqueSelection) {
        categorizedAmenitiesKeys[category] = [];
      }
      values.forEach(({ fieldName, isSelected }) => {
        if (!isUniqueSelection) {
          a[fieldName] = Boolean(isSelected);
        } else if (isSelected) {
          a[category] = fieldName;
        }
        if (isUniqueSelection) {
          categorizedAmenitiesKeys[category].push(fieldName);
        } else {
          categorizedAmenitiesKeys.checkboxes.push(fieldName);
        }
      });
      return a;
    },
    {} as Record<string, boolean | string>,
  );
  return { initialInputs, categorizedAmenitiesKeys };
};

type Props = {
  isRelistingFieldset?: boolean;
};

const AmenitiesPropertyFormField = ({ isRelistingFieldset }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const isOnReviewStep = useIsOnReviewStep();
  const { data: multiplexData } = useMultiplexDetails({ enabled: !isOnReviewStep });
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const isMultiplexFlowActive = Boolean(isInMultiplexListingFlow);
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const { mutateAsync: multiplexUpdateAsync } = useMultiplexUpdate({
    meta: {
      context: {
        api: 'useMultiplexUpdate',
        details: 'AmenitiesPropertyFormField',
        fieldName: 'amenities',
      },
    },
  });

  const propertyFromApi = listingDetails?.amenities?.property ?? [];
  const multiplexPropertyGroups = multiplexData?.amenities?.property;

  const amenityList = useMemo((): Array<AmenityGroup> => {
    if (!isMultiplexFlowActive || isOnReviewStep) {
      return propertyFromApi;
    }

    if (!multiplexPropertyGroups?.length) {
      return propertyFromApi.map((group) => ({
        ...group,
        values: group.values.map((value) => ({ ...value, isSelected: false })),
      }));
    }

    return multiplexPropertyGroups.map((muxGroup) => {
      const matchedGroup = propertyFromApi.find((g) => g.category === muxGroup.category);
      return {
        category: muxGroup.category,
        isEditable: matchedGroup?.isEditable ?? true,
        isRequired: matchedGroup?.isRequired ?? false,
        isUniqueSelection: matchedGroup?.isUniqueSelection ?? false,
        values: muxGroup.values.map((v) => ({
          displayName: v.displayName,
          fieldName: v.fieldName,
          isSelected: v.isSelected,
        })),
      };
    });
  }, [isMultiplexFlowActive, isOnReviewStep, propertyFromApi, multiplexPropertyGroups]);

  const { initialInputs, categorizedAmenitiesKeys } = useMemo(() => buildInitialState(amenityList), [amenityList]);

  const [inputs, setInputs] = useState<Record<string, boolean | string>>(initialInputs);

  const updateMutation = useMultiEditMutation({
    meta: { context: { api: 'multi', details: 'AmenitiesPropertyFormField' } },
  });

  const field = useFieldSave({
    fieldName: 'amenities',
    initialValue: JSON.stringify(initialInputs),
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (jsonValue) => ({
      getParams: { listingAlias },
      postData: getDataToSave({
        inputs: JSON.parse(jsonValue) as Record<string, boolean | string>,
        categorizedAmenitiesKeys,
        listingAlias,
      }),
    }),
  });

  const { saveDebounced: saveMultiplexDebounced, error: multiplexError } = useDebouncedMultiplexAmenitySave({
    listingAlias,
    categorizedAmenitiesKeys,
    multiplexBuildingSessionId,
    multiplexUpdateAsync,
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const valueToSave = categorizedAmenitiesKeys[name] ? value : value === 'true';
    const newInputs = { ...inputs, [name]: valueToSave };
    setInputs(newInputs);

    if (isOnReviewStep || !isInMultiplexListingFlow) {
      field.onChange(JSON.stringify(newInputs));
      return;
    }

    saveMultiplexDebounced(newInputs);
  };

  const displayError = multiplexError ?? field.error;

  return (
    <div data-test="AmenitiesPropertyFormField">
      {displayError && (
        <Alert tone="critical" css={{ marginBottom: '400' }}>
          {displayError}
        </Alert>
      )}
      <Box
        data-testid="AmenitiesPropertyFormFieldContent"
        css={{ display: 'flex', flexDirection: 'row', gap: '400', flexWrap: 'wrap' }}
      >
        {amenityList.map(({ category, isEditable, isRequired, isUniqueSelection, values }) => (
          <Box
            key={`${category}FieldSetContainer`}
            css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: '1 0 280px' }}
          >
            <FieldSet
              disabled={!isEditable}
              required={isRequired && isEditable}
              legend={<Legend size={isRelistingFieldset ? 'lg' : 'md'}>{category}</Legend>}
            >
              {values.map(({ displayName, fieldName }) => {
                const ControlInput = isUniqueSelection ? Radio : Checkbox;
                const label =
                  fieldName === 'hasDisabledAccess' ? (
                    <>
                      <Label>{displayName}</Label>
                      <DisabledAccessAmenityTooltip />
                    </>
                  ) : (
                    <Label>{displayName}</Label>
                  );
                return (
                  <LabeledControl
                    key={displayName}
                    label={label}
                    control={
                      <ControlInput
                        name={isUniqueSelection ? category : fieldName}
                        value={isUniqueSelection ? fieldName : String(!inputs[fieldName])}
                        checked={isUniqueSelection ? inputs[category] === fieldName : Boolean(inputs[fieldName])}
                        onChange={handleChange}
                      />
                    }
                  />
                );
              })}
            </FieldSet>
          </Box>
        ))}
      </Box>
    </div>
  );
};

export default AmenitiesPropertyFormField;
